import { useCallback, useEffect, useMemo } from "react";

import { LabelWrapper, Select, Text, useMemoizedKeyMap } from "@bridgesplit/ui";
import {
    BsMetaUtil,
    getClmmPositionMint,
    getClmmTotalLiquidity,
    isRaydiumMetadata,
    isWhirlpoolMetadata,
    isWhirlpoolPosition,
    useLoopscaleWalletContext,
    useTokenListContext,
    useUserRaydiumPositions,
    useUserWhirlpoolPositions
} from "@bridgesplit/abf-react";
import { ClmmPositionExpanded } from "@bridgesplit/abf-sdk";
import { DispatchType } from "@bridgesplit/react";

import { ClmmPositionSummarized } from "./clmm";

// Define the generic interface for forms that support LP positions
export interface FormWithLpPosition {
    clmmPosition?: ClmmPositionExpanded;
    collateralAmount?: number;
    refetchOutAmount?: boolean;
    labelColor?: "body" | "caption";
}

// Generic LP Position Select component
export function GenericLpPositionSelect<T extends FormWithLpPosition>({
    collateralMint,
    form,
    setForm,
    positions,
    onPositionChange,
    labelColor = "body"
}: {
    collateralMint: string;
    form: T;
    setForm: DispatchType<T>;
    positions?: ClmmPositionExpanded[];
    onPositionChange?: (position: ClmmPositionExpanded) => Partial<T>;
    labelColor?: "body" | "caption";
}) {
    const { getMetadata } = useTokenListContext();
    const metadata = getMetadata(collateralMint);
    const {
        data: { userWalletAddress: user }
    } = useLoopscaleWalletContext();

    const isOrcaPosition = isWhirlpoolMetadata(metadata);
    const isRaydiumPosition = isRaydiumMetadata(metadata);

    const { data: wp } = useUserWhirlpoolPositions({ skip: !isOrcaPosition });
    const { data: raydiumPositions } = useUserRaydiumPositions({ skip: !isRaydiumPosition });

    const availablePositions = useMemo(() => {
        if (positions) return positions;

        if (!user) return [];

        if (isOrcaPosition) {
            return (
                wp?.filter((w) => w.whirlpoolAddress === collateralMint).sort((a, b) => b.totalPrice - a.totalPrice) ??
                []
            );
        }

        if (isRaydiumPosition) {
            return (
                raydiumPositions
                    ?.filter((p) => p.poolAddress === collateralMint)
                    .sort((a, b) => b.totalPrice - a.totalPrice) ?? []
            );
        }

        return [];
    }, [collateralMint, user, wp, raydiumPositions, positions, isOrcaPosition, isRaydiumPosition]);

    const getKey = useCallback((p: ClmmPositionExpanded) => {
        if (isWhirlpoolPosition(p)) {
            return p.position.positionMint;
        }
        // Raydium
        return p.position.nftMint;
    }, []);

    const mintToPosition = useMemoizedKeyMap(availablePositions, getKey);

    const setPosition = useCallback(
        (clmmPosition: ClmmPositionExpanded) => {
            const updates = onPositionChange
                ? onPositionChange(clmmPosition)
                : {
                      clmmPosition,
                      collateralAmount: getClmmTotalLiquidity(clmmPosition),
                      refetchOutAmount: true
                  };

            setForm((prev) => ({
                ...prev,
                ...updates
            }));
        },
        [setForm, onPositionChange]
    );

    // initially sets the position to the defined amount
    useEffect(() => {
        const isNotClmm = !isOrcaPosition && !isRaydiumPosition;
        if (isNotClmm || !availablePositions?.length) return;

        // only set if no position is selected yet
        if (!form.clmmPosition) {
            setPosition(availablePositions[0]);
        }
    }, [collateralMint, isOrcaPosition, isRaydiumPosition, availablePositions, form.clmmPosition, setPosition]);
    if (!isOrcaPosition && !isRaydiumPosition) return null;

    if (availablePositions?.length === 0) {
        return (
            <LabelWrapper color={labelColor} label="Position">
                <Text color="disabled" variant="body2">
                    You don't have any {BsMetaUtil.getName(metadata)} positions
                </Text>
            </LabelWrapper>
        );
    }

    return (
        <LabelWrapper color={labelColor} label="Position">
            <Select
                renderValue={() => <ClmmPositionSummarized position={form.clmmPosition} />}
                value={getClmmPositionMint(form.clmmPosition)}
                loading={!availablePositions}
                setValue={(positionMint) => {
                    const clmmPosition = mintToPosition?.get(positionMint);
                    if (!clmmPosition) return;
                    setPosition(clmmPosition);
                }}
                options={(availablePositions ?? []).map((position) => {
                    if (isWhirlpoolPosition(position)) {
                        return {
                            label: <ClmmPositionSummarized spaceBetween position={position} />,
                            value: position.position.positionMint
                        };
                    }

                    return {
                        label: <ClmmPositionSummarized spaceBetween position={position} />,
                        value: position.position.nftMint
                    };
                })}
            />
        </LabelWrapper>
    );
}
